import Link from 'next/link';
import { FC, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useCMSGameCovers } from '../../../hooks';
import { GameCardWrapper } from '../GameCardWrapper';
import { Category, GameV1 } from '@/api/games';
import { ImageComponent } from '@/components/Image';
import { getGameCover } from '@/helpers/cdn';
import { amountWithCurrencySymbol, getCurrencyName } from '@/helpers/currency';
import { isFavoriteGame } from '@/helpers/game';
import { DEMO_CURRENCY } from '@/helpers/variables';
import { getCurrencyCentsAndCode } from '@/helpers/wallet';
import { Badge } from '@/modules/game/components/Badge';
import { Badges } from '@/modules/game/components/Badges';
import { HeartButton } from '@/modules/game/components/HeartButton';
import { PlayButton } from '@/modules/game/components/PlayButton';
import { TCardMode } from '@/modules/game/types';
import { MAP_LOCALE_TO_CURRENCY } from '@/modules/shared';
import { useCommonStore } from '@/store';

import { ScGameCardBack, ScGameCardFront, ScGameCardInner } from './styled';

interface IGameCardProps {
  game: GameV1;
  mode: TCardMode;
  withoutBottom?: boolean;
  onPlayClick?: () => void;
  onDemoClick?: () => void;
}

export const GameCard: FC<IGameCardProps> = memo(
  ({ game, mode, withoutBottom, onPlayClick, onDemoClick }) => {
    const { id, real, fullPath, listCollections, seoPath } = game;
    const {
      t,
      i18n: { language },
    } = useTranslation('common');

    const favoriteGames = useCommonStore.use.favoriteGames();
    const tournamentGameCategories =
      useCommonStore.use.tournamentGameCategories();
    const tournamentGameIds = useCommonStore.use.tournamentGameIds();
    const currencies = useCommonStore.use.currencies();
    const isAuthenticated = useCommonStore.use.isAuthenticated();

    const isJackpot = useMemo(
      () => listCollections.includes(Category.JACKPOT),
      [listCollections]
    );

    const isFavorite = useMemo(
      () => isFavoriteGame(real, favoriteGames),
      [real, favoriteGames]
    );

    const isTournament = useMemo(() => {
      return (
        tournamentGameCategories.some((item) =>
          listCollections.includes(item)
        ) || tournamentGameIds.includes(seoPath)
      );
    }, [tournamentGameCategories, tournamentGameIds, seoPath]);

    const prize = useMemo(() => {
      const currency =
        MAP_LOCALE_TO_CURRENCY[language as keyof typeof MAP_LOCALE_TO_CURRENCY];
      const jackpot = real[currency]?.jackpot;
      if (!jackpot) return null;
      const { amount } = getCurrencyCentsAndCode(jackpot, currency, currencies);
      return amountWithCurrencySymbol(getCurrencyName(currency), amount);
    }, [real, language]);

    const { covers } = useCMSGameCovers();

    const isDemoAvailable = useMemo(() => {
      return !!game.currencies[DEMO_CURRENCY];
    }, [game.currencies]);

    const url = useMemo(() => {
      return covers?.[id]?.cover?.url || getGameCover(id);
    }, [covers, id]);

    return (
      <GameCardWrapper
        game={game}
        mode={mode}
        withoutBottom={withoutBottom}
        onPlayClick={onPlayClick}
      >
        <ScGameCardInner mode={mode}>
          <ImageComponent
            alt="cover"
            className="cover-image"
            src={url}
            loading="lazy"
            fill
          />
          <ScGameCardBack>
            {isAuthenticated && (
              <HeartButton real={real} isFavorite={isFavorite} />
            )}
            <PlayButton href={`/game/${fullPath}`} onClick={onPlayClick} />
            {isDemoAvailable && (
              <Link
                href={`/game/${fullPath}/?demo=true`}
                className="demo-link"
                onClick={onDemoClick}
              >
                {t('demo')}
              </Link>
            )}
          </ScGameCardBack>
          <ScGameCardFront>
            <Badges
              isJackpot={isJackpot}
              isTournament={isTournament}
              listCollections={listCollections}
            />
            {isFavorite && <Badge className="heart-image" type="heart" />}
            {prize && isJackpot && (
              <div className="tournament-bottom">
                <ImageComponent
                  className="tournament-image"
                  src="/jackpot/diamond.webp"
                  width={160}
                  height={160}
                  alt="diamond"
                />
                <span>{prize}</span>
              </div>
            )}
          </ScGameCardFront>
        </ScGameCardInner>
      </GameCardWrapper>
    );
  }
);
